'use client'

import { useEffect, useState } from 'react'

type FatalErrorLocale = 'ko' | 'en' | 'ja'

const FATAL_ERROR_COPY: Record<FatalErrorLocale, { title: string; description: string }> = {
  ko: {
    title: '일시적인 오류가 발생했습니다.',
    description: '앱을 종료한 후 다시 실행해 주세요.',
  },
  en: {
    title: 'A temporary error has occurred.',
    description: 'Please close and reopen the app.',
  },
  ja: {
    title: '一時的なエラーが発生しました。',
    description: 'アプリを終了して、もう一度起動してください。',
  },
}

const normalizeFatalErrorLocale = (value: string | null | undefined): FatalErrorLocale | null => {
  const locale = value?.trim().toLowerCase().split('-')[0]
  return locale === 'ko' || locale === 'en' || locale === 'ja' ? locale : null
}

export const resolveFatalErrorLocale = (): FatalErrorLocale => {
  if (typeof document === 'undefined') {
    return 'en'
  }

  try {
    const effectiveLocaleCookie = document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith('PODO_EFFECTIVE_LOCALE='))
      ?.slice('PODO_EFFECTIVE_LOCALE='.length)
    const candidates = [
      effectiveLocaleCookie ? decodeURIComponent(effectiveLocaleCookie) : null,
      document.documentElement.lang,
      typeof navigator === 'undefined' ? null : navigator.language,
    ]

    for (const candidate of candidates) {
      const locale = normalizeFatalErrorLocale(candidate)
      if (locale) {
        return locale
      }
    }
  } catch {
    // 치명적 오류 화면은 locale 복구 실패까지 감당해야 하므로 영문 폴백만 남긴다.
  }

  return 'en'
}

export const FatalErrorContent = ({ locale }: { locale: FatalErrorLocale }) => {
  const copy = FATAL_ERROR_COPY[locale]

  return (
    <main
      style={{
        alignItems: 'center',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        justifyContent: 'center',
        minHeight: '100dvh',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <h1
        style={{
          color: '#1c1c1c',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontSize: 20,
          lineHeight: 1.4,
          margin: 0,
        }}
      >
        {copy.title}
      </h1>
      <p
        style={{
          color: '#737373',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          fontSize: 16,
          lineHeight: 1.5,
          margin: 0,
        }}
      >
        {copy.description}
      </p>
    </main>
  )
}

const GlobalError = ({ error }: { error: Error }) => {
  // 서버와 첫 클라이언트 렌더는 같은 영문으로 고정해 오류 화면에서 hydration 오류가 재발하지 않게 한다.
  const [locale, setLocale] = useState<FatalErrorLocale>('en')

  useEffect(() => {
    setLocale(resolveFatalErrorLocale())
  }, [])

  useEffect(() => {
    // 오류 화면 렌더가 로거 청크에 의존하지 않도록 화면을 먼저 띄운 뒤 최선 노력으로만 기록한다.
    void import('@shared/libs/logger/client-error-capture')
      .then(({ captureClientException }) => captureClientException(error, { source: 'error-boundary:global' }))
      .catch(() => undefined)
  }, [error])

  return (
    <html lang={locale} suppressHydrationWarning>
      <body style={{ margin: 0 }}>
        <FatalErrorContent locale={locale} />
      </body>
    </html>
  )
}

export default GlobalError
